import React, { useState, useMemo, useEffect } from 'react';
import api from '../services/api';

const CHAT_API_BASE = '/api/chat';

async function authFetch(url, opts = {}) {
  const token = await api.ensureAuthenticated();
  return fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
}

const currentUserId = (() => {
  try { return JSON.parse(sessionStorage.getItem('user') || 'null')?.id || null; }
  catch (e) { return null; }
})();

const fmtCurrency = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

let rowSeq = 0;
const blankRow = () => ({
  key: `row-${++rowSeq}`,
  component_product_id: '',
  name: '',
  quantity: '1',
  unit_price: '',
  unit_cost: '',
});

/**
 * Create a product assembly — a catalog product whose price/cost is the
 * rolled-up sum of its components (catalog products + free-form lines like
 * Labor). Saving creates the AOS_Products record, then POSTs the components
 * to product-assemblies.php which recomputes and writes back price/cost.
 */
const ProductAssemblyForm = ({ products = [], categories = [], editingProduct = null, onCancel, onSaved }) => {
  const isEditing = !!editingProduct?.id;
  const ea = editingProduct?.attributes || {};
  const [name, setName] = useState(ea.name || '');
  const [partNumber, setPartNumber] = useState(ea.part_number || '');
  const [category, setCategory] = useState(ea.category || '');
  const [description, setDescription] = useState(ea.description || '');
  const [rows, setRows] = useState([blankRow(), blankRow()]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [image, setImage] = useState(null); // { name, dataUrl }
  const [loadingComponents, setLoadingComponents] = useState(isEditing);

  // When editing an existing assembly, load its saved components into the rows.
  useEffect(() => {
    if (!isEditing) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${CHAT_API_BASE}/product-assemblies.php?assembly_product_id=${encodeURIComponent(editingProduct.id)}`);
        const data = await res.json();
        if (!cancelled && data.success && Array.isArray(data.components)) {
          const loaded = data.components.map((c) => ({
            key: `row-${++rowSeq}`,
            component_product_id: c.component_product_id || '',
            name: c.name || '',
            quantity: c.quantity != null ? String(parseFloat(c.quantity)) : '1',
            unit_price: c.unit_price != null && c.unit_price !== '' ? String(parseFloat(c.unit_price)) : '',
            unit_cost: c.unit_cost != null && c.unit_cost !== '' ? String(parseFloat(c.unit_cost)) : '',
          }));
          setRows(loaded.length ? loaded : [blankRow(), blankRow()]);
        }
      } catch (e) {
        console.error('Failed to load assembly components:', e);
      }
      if (!cancelled) setLoadingComponents(false);
    })();
    return () => { cancelled = true; };
  }, [isEditing, editingProduct]);

  const onPickImage = (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please choose an image file.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImage({ name: file.name, dataUrl: reader.result });
    reader.readAsDataURL(file);
  };

  const updateRow = (key, changes) => {
    setRows(prev => prev.map(r => (r.key === key ? { ...r, ...changes } : r)));
  };

  const addRow = () => setRows(prev => [...prev, blankRow()]);
  const removeRow = (key) => setRows(prev => (prev.length > 1 ? prev.filter(r => r.key !== key) : prev));

  // Picking a catalog product fills name/price/cost (still editable). Choosing
  // the blank option turns the row back into a free-form line.
  const onPickProduct = (key, productId) => {
    if (!productId) {
      updateRow(key, { component_product_id: '' });
      return;
    }
    const p = products.find(pr => pr.id === productId);
    const a = p?.attributes || {};
    updateRow(key, {
      component_product_id: productId,
      name: a.name || '',
      unit_price: a.price != null && a.price !== '' ? String(a.price) : '',
      unit_cost: a.cost != null && a.cost !== '' ? String(a.cost) : '',
    });
  };

  const { totalPrice, totalCost } = useMemo(() => {
    let tp = 0, tc = 0;
    rows.forEach(r => {
      const qty = parseFloat(r.quantity) || 0;
      tp += (parseFloat(r.unit_price) || 0) * qty;
      tc += (parseFloat(r.unit_cost) || 0) * qty;
    });
    return { totalPrice: Math.round(tp * 100) / 100, totalCost: Math.round(tc * 100) / 100 };
  }, [rows]);

  const handleSave = async () => {
    setMessage(null);
    if (loadingComponents) return; // don't save until existing components load
    const cleanName = name.trim();
    if (!cleanName) {
      setMessage({ type: 'error', text: 'Assembly name is required.' });
      return;
    }
    const components = rows
      .filter(r => r.name.trim() !== '')
      .map(r => ({
        component_product_id: r.component_product_id || null,
        name: r.name.trim(),
        quantity: parseFloat(r.quantity) || 0,
        unit_price: parseFloat(r.unit_price) || 0,
        unit_cost: parseFloat(r.unit_cost) || 0,
      }));
    if (components.length === 0) {
      setMessage({ type: 'error', text: 'Add at least one component.' });
      return;
    }

    setSaving(true);
    try {
      // 1) Create or update the catalog product carrying the rolled-up totals.
      const productPayload = {
        name: cleanName,
        part_number: partNumber.trim(),
        description: description.trim(),
        type: 'Good',
        price: totalPrice,
        cost: totalCost,
      };
      if (category.trim()) productPayload.category = category.trim();

      let recordId;
      if (isEditing) {
        await api.updateRecord('AOS_Products', editingProduct.id, productPayload);
        recordId = editingProduct.id;
      } else {
        const result = await api.createRecord('AOS_Products', productPayload);
        recordId = result?.data?.id || result?.id;
      }
      if (!recordId) {
        throw new Error('Product was saved but no id was returned.');
      }

      // 2) Save the component list; the endpoint recomputes and writes back
      //    price/cost and marks the product as an assembly.
      const res = await authFetch(`${CHAT_API_BASE}/product-assemblies.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assembly_product_id: recordId,
          components,
          user_id: currentUserId,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to save assembly components.');
      }

      // 3) Optional image — stored as a product image so it shows on the tile.
      if (image?.dataUrl) {
        try {
          await fetch(`${CHAT_API_BASE}/product-images.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              product_id: recordId,
              image_data: image.dataUrl,
              image_name: image.name || 'assembly.jpg',
            }),
          });
        } catch (imgErr) {
          console.error('Assembly image upload failed:', imgErr);
        }
      }

      setMessage({ type: 'success', text: isEditing ? 'Assembly updated successfully!' : 'Assembly created successfully!' });
      setTimeout(() => { if (onSaved) onSaved(); }, 1200);
    } catch (err) {
      console.error('Failed to save assembly:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to save assembly. Please try again.' });
      setSaving(false);
    }
  };

  return (
    <div className="edit-form-container">
      <div className="edit-form-header">
        <h2>{isEditing ? 'Edit Product Assembly' : 'Create Product Assembly'}</h2>
        <span className="editing-id">Bundled at a rolled-up price</span>
      </div>

      {message && (
        <div className={`save-message ${message.type}`}>{message.text}</div>
      )}

      <div className="edit-form assembly-form">
        <div className="form-group">
          <label className="form-label">Assembly Name<span className="required">*</span></label>
          <input
            className="form-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Drapery Package — Master Bedroom"
          />
        </div>

        <div className="form-group-row">
          <div className="form-group form-group-half">
            <label className="form-label">Part Number</label>
            <input
              className="form-input"
              type="text"
              value={partNumber}
              onChange={(e) => setPartNumber(e.target.value)}
            />
          </div>
          <div className="form-group form-group-half">
            <label className="form-label">Category</label>
            <input
              className="form-input"
              type="text"
              list="assembly-category-options"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Optional"
            />
            <datalist id="assembly-category-options">
              {categories.map(c => <option key={c} value={c} />)}
            </datalist>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea
            className="form-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </div>

        <div className="form-group">
          <label className="form-label">Image</label>
          <div className="assembly-image-field">
            {image?.dataUrl && (
              <img className="assembly-image-preview" src={image.dataUrl} alt={image.name || 'preview'} />
            )}
            <div className="assembly-image-controls">
              <label className="assembly-image-btn">
                {image ? 'Change Image' : 'Choose Image'}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={onPickImage}
                />
              </label>
              {image && (
                <button type="button" className="assembly-image-remove" onClick={() => setImage(null)}>
                  Remove
                </button>
              )}
              <span className="assembly-image-hint">Shown on the product tile.</span>
            </div>
          </div>
        </div>

        <div className="assembly-components">
          <label className="form-label">Components</label>
          <div className="assembly-components-scroll">
          <table className="assembly-components-table">
            <thead>
              <tr>
                <th style={{ width: '240px' }}>Catalog Product</th>
                <th style={{ minWidth: '260px' }}>Description</th>
                <th style={{ width: '72px' }}>Qty</th>
                <th style={{ width: '124px' }}>Unit Price</th>
                <th style={{ width: '124px' }}>Unit Cost</th>
                <th style={{ width: '96px' }}>Line Total</th>
                <th style={{ width: '34px' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const qty = parseFloat(r.quantity) || 0;
                const lineTotal = (parseFloat(r.unit_price) || 0) * qty;
                return (
                  <tr key={r.key}>
                    <td>
                      <select
                        className="form-select"
                        value={r.component_product_id}
                        onChange={(e) => onPickProduct(r.key, e.target.value)}
                      >
                        <option value="">— Free-form —</option>
                        {products.map(p => (
                          <option key={p.id} value={p.id}>{p.attributes?.name || '(unnamed)'}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="form-input"
                        type="text"
                        value={r.name}
                        onChange={(e) => updateRow(r.key, { name: e.target.value })}
                        placeholder="e.g. Labor — installation"
                      />
                    </td>
                    <td>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        step="any"
                        value={r.quantity}
                        onChange={(e) => updateRow(r.key, { quantity: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={r.unit_price}
                        onChange={(e) => updateRow(r.key, { unit_price: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        className="form-input"
                        type="number"
                        min="0"
                        step="0.01"
                        value={r.unit_cost}
                        onChange={(e) => updateRow(r.key, { unit_cost: e.target.value })}
                      />
                    </td>
                    <td className="assembly-line-total">{fmtCurrency(lineTotal)}</td>
                    <td>
                      <button
                        type="button"
                        className="assembly-remove-row"
                        onClick={() => removeRow(r.key)}
                        title="Remove component"
                      >×</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <button type="button" className="assembly-add-row" onClick={addRow}>+ Add Component</button>

          <div className="assembly-totals">
            <div className="assembly-total-line">
              <span>Total Cost</span>
              <strong>{fmtCurrency(totalCost)}</strong>
            </div>
            <div className="assembly-total-line assembly-total-price">
              <span>Rolled-up Price</span>
              <strong>{fmtCurrency(totalPrice)}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="edit-form-actions">
        <button className="cancel-btn" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="save-btn" onClick={handleSave} disabled={saving || loadingComponents}>
          {saving ? 'Saving...' : (loadingComponents ? 'Loading…' : (isEditing ? 'Save Assembly' : 'Create Assembly'))}
        </button>
      </div>
    </div>
  );
};

export default ProductAssemblyForm;
